/**
 * Utility functions for user role and permission checks
 */
import userImpersonationService from '../services/userImpersonationService';
import { hasSchedulingIssue } from './orderUtils';

const ADMIN_SCHEDULING_WARNING_ROLES = ['admin', 'superadmin'];
const CART_VAT_TOGGLE_ROLES = ['admin', 'superadmin', 'dispatcher', 'distpacher'];
const CLIENT_ROLE_NAMES = ['customer', 'agency'];
const CLIENT_MENU_PERMISSIONS = [
  'view_dashboard',
  'view_properties',
  'view_orders',
  'view_reports',
  'view_inbox',
];

const safeJsonParse = (value, fallback) => {
  try {
    return JSON.parse(value || JSON.stringify(fallback));
  } catch (error) {
    return fallback;
  }
};

const normalizeRoleName = (role) => {
  if (!role) return null;
  return typeof role === 'string' ? role : role.name;
};

const getRoleNamesFromCurrentUser = () => {
  const user = safeJsonParse(localStorage.getItem('user'), {});

  return [
    user.role,
    ...(Array.isArray(user.roles) ? user.roles.map(normalizeRoleName) : [])
  ].filter(Boolean);
};

const getStoredRoleNames = () => {
  const userRolesFull = safeJsonParse(localStorage.getItem('user_roles_full'), []);
  const userRoles = safeJsonParse(localStorage.getItem('user_roles'), []);

  return [
    ...(Array.isArray(userRolesFull) ? userRolesFull.map(normalizeRoleName) : []),
    ...(Array.isArray(userRoles) ? userRoles.map(normalizeRoleName) : [])
  ].filter(Boolean);
};

const getCurrentRoleNames = () => {
  return [
    ...getRoleNamesFromCurrentUser(),
    ...getStoredRoleNames()
  ].filter(Boolean);
};

const hasAnyRoleName = (allowedRoles, roleNames = getCurrentRoleNames()) => {
  const normalizedAllowedRoles = allowedRoles.map(roleName => String(roleName).toLowerCase());

  return roleNames.some(roleName =>
    normalizedAllowedRoles.includes(String(roleName).toLowerCase())
  );
};

const hasAdminOrSuperadminRoleName = (roleNames = getCurrentRoleNames()) => {
  return hasAnyRoleName(ADMIN_SCHEDULING_WARNING_ROLES, roleNames);
};

const hasClientRoleName = (roleNames = getCurrentRoleNames()) => {
  return hasAnyRoleName(CLIENT_ROLE_NAMES, roleNames);
};

const getImpersonatingAdminRoleNames = () => {
  if (!userImpersonationService.isImpersonating()) {
    return [];
  }

  const adminUser = userImpersonationService.getAdminUser() || {};
  const adminRolesFull = safeJsonParse(localStorage.getItem('_admin_roles_full'), []);
  const adminRoles = safeJsonParse(localStorage.getItem('_admin_roles'), []);

  return [
    adminUser.role,
    ...(Array.isArray(adminUser.roles) ? adminUser.roles.map(normalizeRoleName) : []),
    ...(Array.isArray(adminRolesFull) ? adminRolesFull.map(normalizeRoleName) : []),
    ...(Array.isArray(adminRoles) ? adminRoles.map(normalizeRoleName) : [])
  ].filter(Boolean);
};

const getFallbackPermissionsForCurrentUser = () => {
  return hasClientRoleName() ? CLIENT_MENU_PERMISSIONS : [];
};

const userRoleUtils = {
  /**
   * Check if the current user is an admin or impersonating a user
   * @returns {boolean} True if user is admin or admin is impersonating a user
   */
  isAdminOrImpersonating: () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isAdmin = user.role === 'admin' || userRoleUtils.hasAdminRole();
    
    // If currently impersonating and the original user is an admin
    if (userImpersonationService.isImpersonating()) {
      const adminUser = userImpersonationService.getAdminUser();
      return adminUser && (adminUser.role === 'admin' || userRoleUtils.adminHadAdminRole());
    }

    
    return isAdmin;
  },

  /**
   * Check if the current user is an admin
   * @returns {boolean} True if user is admin
   */
  isAdmin: () => {
    // Get the current user
    const user_roles = safeJsonParse(localStorage.getItem('user_roles_full'), []);

    if (user_roles && user_roles.length > 0) {
      // Check if the user has an admin role. Agency can have admin-like flags but
      // must still behave as a client for admin-only warning surfaces.
      return user_roles.some(role =>
        role.name === 'admin' ||
        role.name === 'superadmin' ||
        (role.is_admin === 1 && role.name !== 'agency')
      );
    }
    if (hasAdminOrSuperadminRoleName()) {
      return true;
    }
    // If no roles found, return false
    return false;
  },

  /**
   * Scheduling warnings are internal ops signals and should never be exposed to
   * customers/agencies, including during admin impersonation.
   * @returns {boolean} True only for current admin/superadmin sessions
   */
  canViewSchedulingWarnings: () => {
    if (userImpersonationService.isImpersonating()) {
      const userRoles = safeJsonParse(localStorage.getItem('user_roles'), []);
      const visibleRoleNames = [
        ...getRoleNamesFromCurrentUser(),
        ...(Array.isArray(userRoles) ? userRoles.map(normalizeRoleName) : [])
      ].filter(Boolean);

      return visibleRoleNames.length > 0 && hasAdminOrSuperadminRoleName(visibleRoleNames);
    }

    return hasAdminOrSuperadminRoleName();
  },

  /**
   * Sales/order comparison widgets contain internal business metrics. Show
   * them only in an effective admin/superadmin session. While impersonating,
   * use the impersonated user's visible roles so the preview matches what a
   * customer or agency can actually see.
   * @returns {boolean} True only for the currently visible admin/superadmin session
   */
  canViewDashboardComparisons: () => {
    if (userImpersonationService.isImpersonating()) {
      const userRoles = safeJsonParse(localStorage.getItem('user_roles'), []);
      const visibleRoleNames = [
        ...getRoleNamesFromCurrentUser(),
        ...(Array.isArray(userRoles) ? userRoles.map(normalizeRoleName) : [])
      ].filter(Boolean);

      return visibleRoleNames.length > 0 && hasAdminOrSuperadminRoleName(visibleRoleNames);
    }

    return hasAdminOrSuperadminRoleName();
  },

  /**
   * Property activation is an internal admin action.
   * @returns {boolean} True only for admin/superadmin sessions
   */
  canManagePropertyStatus: () => {
    return hasAdminOrSuperadminRoleName();
  },

  canManageSchedoxSync: () => userRoleUtils.canViewSchedulingWarnings(),

  canManagePropertyRecurrence: () => userRoleUtils.canViewSchedulingWarnings() ||
    hasAnyRoleName(ADMIN_SCHEDULING_WARNING_ROLES, getImpersonatingAdminRoleNames()),

  /**
   * Cart VAT view toggle is an internal pricing aid for selected roles only.
   * @returns {boolean} True for admin, superadmin, dispatcher, or legacy misspelled distpacher roles
   */
  canViewCartVatToggle: () => {
    return hasAnyRoleName(CART_VAT_TOGGLE_ROLES) ||
      hasAnyRoleName(CART_VAT_TOGGLE_ROLES, getImpersonatingAdminRoleNames());
  },

  /**
   * Check if an order has scheduling issues (missing task ID)
   * @param {Object} order - Order record
   * @returns {boolean} - True if order has scheduling issues
   */
  hasSchedulingIssue,

  /**
   * Check if the current session is impersonating a user
   * @returns {boolean} True if session is impersonating
   */
  isImpersonating: () => {
    return userImpersonationService.isImpersonating();
  },

  /**
   * Check if user has a specific permission
   * @param {string} permissionName - Name of the permission to check
   * @returns {boolean} True if user has the permission
   */
  hasPermission: (permissionName) => {
    // Get stored permissions from localStorage
    const userPermissions = JSON.parse(localStorage.getItem('user_permissions') || '[]');
    
    // Admin users have all permissions
    if (userRoleUtils.isAdmin()) {
      return true;
    }
    
    // If impersonating, use the impersonated user's permissions
    if (userRoleUtils.isImpersonating() && userRoleUtils.isAdmin()) {
      // An admin impersonating a user still has all permissions
      return true;
    }
    
    // Check specific permission
    return userPermissions.includes(permissionName) ||
      getFallbackPermissionsForCurrentUser().includes(permissionName);
  },
  
  /**
   * Check if user has any of the specified permissions
   * @param {Array} permissionNames - Array of permission names
   * @returns {boolean} True if user has any of the permissions
   */
  hasAnyPermission: (permissionNames) => {
    // Admin users have all permissions
    if (userRoleUtils.isAdmin()) {
      return true;
    }
    
    // If impersonating, use the impersonated user's permissions
    if (userRoleUtils.isImpersonating() && userRoleUtils.isAdmin()) {
      // An admin impersonating a user still has all permissions
      return true;
    }
    
    // Get stored permissions from localStorage
    const userPermissions = JSON.parse(localStorage.getItem('user_permissions') || '[]');
    
    // Check if user has any of the specified permissions
    return permissionNames.some(permission => userPermissions.includes(permission));
  },
  
  /**
   * Check if user has all of the specified permissions
   * @param {Array} permissionNames - Array of permission names
   * @returns {boolean} True if user has all of the permissions
   */
  hasAllPermissions: (permissionNames) => {
    // Admin users have all permissions
    if (userRoleUtils.isAdmin()) {
      return true;
    }
    
    // If impersonating, use the impersonated user's permissions
    if (userRoleUtils.isImpersonating() && userRoleUtils.isAdmin()) {
      // An admin impersonating a user still has all permissions
      return true;
    }
    
    // Get stored permissions from localStorage
    const userPermissions = JSON.parse(localStorage.getItem('user_permissions') || '[]');
    
    // Check if user has all of the specified permissions
    return permissionNames.every(permission => userPermissions.includes(permission));
  },

  /**
   * Get user's roles
   * @returns {Array} Array of user role names
   */
  getUserRoles: () => {
    const userRoles = JSON.parse(localStorage.getItem('user_roles') || '[]');
    
    // If admin is impersonating, add admin role
    if (userRoleUtils.isImpersonating() && userRoleUtils.isAdmin()) {
      // Make sure 'admin' is included in the roles if the original user is an admin
      if (!userRoles.includes('admin')) {
        return [...userRoles, 'admin'];
      }
    }
    
    return userRoles;
  },

  /**
   * Check if current user is a client-facing customer or agency user.
   * @returns {boolean} True if user should get the standard client menu
   */
  hasClientRole: () => {
    return hasClientRoleName();
  },

  /**
   * Read-only client menu permissions used while live permissions bootstrap.
   * @returns {Array} Array of fallback permission names
   */
  getFallbackPermissionsForCurrentUser: () => {
    return getFallbackPermissionsForCurrentUser();
  },

  /**
   * Check if user has a specific role
   * @param {string} roleName - Name of the role to check
   * @returns {boolean} True if user has the role
   */
  hasRole: (roleName) => {
    // Special case for admin role
    if (roleName === 'admin' && userRoleUtils.isAdmin()) {
      return true;
    }
    
    // Get stored roles from localStorage
    const userRoles = JSON.parse(localStorage.getItem('user_roles') || '[]');
    
    // If impersonating, check admin roles as well
    if (userRoleUtils.isImpersonating() && roleName === 'admin') {
      const adminUser = userImpersonationService.getAdminUser();
      if (adminUser && adminUser.role === 'admin') {
        return true;
      }
    }
    
    // Check specific role
    return userRoles.includes(roleName);
  },

  /**
   * Check if user has a role with is_admin flag
   * @returns {boolean} True if user has an admin role
   */
  hasAdminRole: () => {
    // Get user roles object that includes the is_admin flag
    const userRolesObj = JSON.parse(localStorage.getItem('user_roles_full') || '[]');
    
    // Check if any role has is_admin = 1 but exclude agency roles
    const hasAdmin = userRolesObj.some(role => role.is_admin === 1 && role.name != 'agency');

   
    return hasAdmin;
  },

  /**
   * Check if one of roles is agency
   * @returns {boolean} True if user has an agency role
   */
  hasAgencyRole: () => {
    // Get user roles object that includes the is_admin flag
    const userRolesObj = JSON.parse(localStorage.getItem('user_roles_full') || '[]');
    
    // Check if any role has is_agency = 1
    return userRolesObj.some(role => role.name === 'agency' || role.is_agency === 1);
  
  },

  /**
   * Check if the admin user had an admin role when impersonating
   * @returns {boolean} True if admin had an admin role
   */
  adminHadAdminRole: () => {
    if (!userRoleUtils.isImpersonating()) {
      return false;
    }
    
    // Get admin roles object
    const adminRolesObj = JSON.parse(localStorage.getItem('_admin_roles_full') || '[]');
    
    // Check if any admin role has is_admin = 1 but exclude agency roles
    return adminRolesObj.some(role => role.is_admin === 1 && role.name != 'agency');
  },

/**
 * Get minimum date allowed for orders based on user role
 * Regular users must order at least 24 hours in advance (next day before 3pm, or day after if after 3pm)
 * Admins and impersonated users can order for any date including past dates
 * @returns {string} ISO date string for minimum allowed date
 */
getMinimumOrderDate: () => {
  // If admin or impersonating, allow any date including past dates
  if (userRoleUtils.isAdminOrImpersonating()) {
    // For admins, we'll return today's date in London timezone
    const londonToday = new Date().toLocaleDateString('en-CA', { 
      timeZone: 'Europe/London' 
    });
    return londonToday;
  }

  // For regular users, enforce the standard restrictions using London time
  const cutoffHour = 15; // 3 PM London time
  
  // Get current time in London
  const londonTime = new Date().toLocaleString('en-GB', { 
    timeZone: 'Europe/London',
    hour12: false 
  });
  
  // Parse London time to get current hour
  const currentLondonHour = parseInt(londonTime.split(', ')[1].split(':')[0]);
  
  console.log(`Current London time: ${londonTime}`);
  console.log(`Current London hour: ${currentLondonHour}`);
  
  // Calculate minimum date based on London time
  let minDate = new Date();
  
  // If current London time is before 3 PM, set for tomorrow
  // If after 3 PM, set for day after tomorrow  
  if (currentLondonHour >= cutoffHour) {
    console.log("Current London time is after 3 PM, setting minimum date to day after tomorrow");
    minDate.setDate(minDate.getDate() + 2);
  } else {
    console.log("Current London time is before 3 PM, setting minimum date to tomorrow");
    minDate.setDate(minDate.getDate() + 1);
  }

  // Convert to London date and format as YYYY-MM-DD
  const minDateString = minDate.toLocaleDateString('en-CA', { 
    timeZone: 'Europe/London' 
  });
  
  console.log("Minimum order date (based on London time):", minDateString);
  
  return minDateString;
},

  /**
   * Get the effective date for an order
   * If user is admin or impersonating, returns the original date
   * If user is regular and date is before minimum, returns minimum date
   * @param {string} originalDate - The requested date
   * @returns {string} ISO date string for effective date
   */
  getEffectiveOrderDate: (originalDate) => {
    // Skip restriction for admins or impersonated users
    if (userRoleUtils.isAdminOrImpersonating()) {
      return originalDate;
    }
    
    const minDate = userRoleUtils.getMinimumOrderDate();
    
    // For regular users, enforce minimum date
    if (new Date(originalDate) < new Date(minDate)) {
      return minDate;
    }
    
    return originalDate;
  },

  /**
   * Get default date for order creation based on user role
   * - For admins and impersonated users: Today's date
   * - For regular users: The minimum allowed date
   * @returns {string} ISO date string for default order date
   */
  getDefaultOrderDate: () => {
    // For admin users, default to today
    if (userRoleUtils.isAdminOrImpersonating()) {
      const today = new Date();
      return today.toLocaleDateString('en-CA'); // YYYY-MM-DD format in local timezone
    }
    
    // For regular users, use minimum date (tomorrow or day after tomorrow)
    return userRoleUtils.getMinimumOrderDate();
  },

  /**
   * Calculate default completion time based on arrival time
   * Ensures at least 1.5 hours difference between arrival and completion
   * @param {string} arrivalTime - Arrival time in HH:MM format
   * @returns {string} Completion time in HH:MM format
   */
  calculateDefaultCompletionTime: (arrivalTime) => {
    if (!arrivalTime) return "09:30"; // Default completion time
    
    try {
      const [hours, minutes] = arrivalTime.split(':').map(Number);
      
      // Convert to decimal hours
      const arrivalDecimal = hours + (minutes / 60);
      
      // Add 1.5 hours
      let completionDecimal = arrivalDecimal + 1.5;
      
      // Calculate hours and minutes
      const completionHours = Math.floor(completionDecimal);
      let completionMinutes = Math.round((completionDecimal - completionHours) * 60);
      
      // Round to nearest 15 minutes
      completionMinutes = Math.round(completionMinutes / 15) * 15;
      
      // Format and return
      return `${completionHours.toString().padStart(2, '0')}:${completionMinutes.toString().padStart(2, '0')}`;
    } catch (error) {
      console.error("Error calculating completion time:", error);
      return "09:30"; // Default to 09:30 if calculation fails
    }
  }
};

export default userRoleUtils;
